import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, onAuthStateChanged, 
  signOut as firebaseSignOut,
  User as FirebaseUser
} from 'firebase/auth';

// Define extended user interface
interface User {
  id: string;
  email: string | null;
  firstName?: string;
  lastName?: string;
  isVerified?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  systemRole?: 'admin';
  [key: string]: any; // For any additional user fields
}

interface ImpersonationStatus {
  isImpersonating: boolean;
  originalAdmin?: User;
  impersonatedUser?: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  impersonationStatus: ImpersonationStatus;
  setUser: (userData: any) => void;
  setImpersonationStatus: (status: ImpersonationStatus) => void;
  refreshContext: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  loading: true,
  impersonationStatus: { isImpersonating: false },
  setUser: () => {},
  setImpersonationStatus: () => {},
  refreshContext: async () => {},
  logout: async () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonationStatus, setImpersonationStatus] = useState<ImpersonationStatus>({ isImpersonating: false });
  const auth = getAuth();

  // Handle additional user data
  const setUserData = (userData: any) => {
    if (userData) {
      // Store user data in localStorage
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(current => {
        // Only update if the data has actually changed
        if (!current || JSON.stringify(current) !== JSON.stringify({...current, ...userData})) {
          return {
            ...current,
            ...userData
          };
        }
        return current;
      });
    }
  };

  // Check for active impersonation on load
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonationData = localStorage.getItem('impersonationData');
      if (impersonationData) {
        try {
          const data = JSON.parse(impersonationData);
          setImpersonationStatus({
            isImpersonating: true,
            originalAdmin: data.originalAdminData,
            impersonatedUser: data.impersonatedUser
          });
        } catch (error) {
          console.error('Error parsing impersonation data:', error);
          localStorage.removeItem('impersonationData');
        }
      }
    };
    
    checkImpersonation();
  }, []);

  // Convert Firebase user to our User type and merge with additional data
  const processUser = (firebaseUser: FirebaseUser | null): User | null => {
    if (!firebaseUser) return null;

    // Check for impersonation data first
    const impersonationData = localStorage.getItem('impersonationData');
    if (impersonationData) {
      try {
        const { impersonatedUser } = JSON.parse(impersonationData);
        return impersonatedUser;
      } catch (error) {
        console.error('Error parsing impersonation data:', error);
        localStorage.removeItem('impersonationData');
      }
    }

    // Get additional data from localStorage if available
    const additionalData = localStorage.getItem('userData');
    let parsedData = {};
    
    if (additionalData) {
      try {
        const cachedData = JSON.parse(additionalData);
        // Only use cached data if it belongs to the current user OR if we can't determine the user ID
        if (cachedData && (cachedData.id === firebaseUser.uid || !cachedData.id)) {
          parsedData = cachedData;
        } else if (cachedData.id && cachedData.id !== firebaseUser.uid) {
          // Clear stale cached data for a different user
          console.log('Clearing stale cached data for different user');
          localStorage.removeItem('userData');
        }
      } catch (e) {
        console.error('Error parsing stored user data:', e);
        localStorage.removeItem('userData');
      }
    }

    return {
      id: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified,
      ...parsedData
    };
  };

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clear session storage flags when auth state changes
      sessionStorage.removeItem('hasLoggedCacheMessage');
      
      if (firebaseUser) {
        // For existing users, try to get fresh data from Firestore
        try {
          const { getCurrentUser } = await import('../services/authService');
          const freshUserData = await getCurrentUser();
          
          if (freshUserData) {
            console.log('AuthContext - Fresh user data from getCurrentUser:', freshUserData);
            setUser(freshUserData);
          } else {
            console.log('AuthContext - No fresh user data, using processed Firebase user');
            setUser(processUser(firebaseUser));
          }
        } catch (error) {
          console.error('Error fetching fresh user data on auth change:', error);
          setUser(processUser(firebaseUser));
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Listen for impersonation changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'impersonationData') {
        // Recalculate impersonation status
        const checkImpersonation = () => {
          const impersonationData = localStorage.getItem('impersonationData');
          if (impersonationData) {
            try {
              const data = JSON.parse(impersonationData);
              setImpersonationStatus({
                isImpersonating: true,
                originalAdmin: data.originalAdminData,
                impersonatedUser: data.impersonatedUser
              });
              // Update user to impersonated user
              setUser(data.impersonatedUser);
            } catch (error) {
              console.error('Error parsing impersonation data:', error);
              localStorage.removeItem('impersonationData');
            }
          } else {
            setImpersonationStatus({ isImpersonating: false });
            // Reprocess current Firebase user
            if (auth.currentUser) {
              setUser(processUser(auth.currentUser));
            }
          }
        };
        
        checkImpersonation();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [auth]);

  // Function to manually refresh context after impersonation changes
  const refreshContext = async () => {
    const impersonationData = localStorage.getItem('impersonationData');
    if (impersonationData) {
      try {
        const data = JSON.parse(impersonationData);
        setImpersonationStatus({
          isImpersonating: true,
          originalAdmin: data.originalAdminData,
          impersonatedUser: data.impersonatedUser
        });
        setUser(data.impersonatedUser);
      } catch (error) {
        console.error('Error parsing impersonation data:', error);
        localStorage.removeItem('impersonationData');
      }
    } else {
      setImpersonationStatus({ isImpersonating: false });
      // Refresh user data from getCurrentUser which will fetch from Firestore
      if (auth.currentUser) {
        try {
          // Import getCurrentUser dynamically to avoid circular imports
          const { getCurrentUser } = await import('../services/authService');
          const freshUserData = await getCurrentUser();
          
          if (freshUserData) {
            setUser(freshUserData);
          } else {
            // Fallback to processing current Firebase user
            setUser(processUser(auth.currentUser));
          }
        } catch (error) {
          console.error('Error refreshing user data:', error);
          // Fallback to processing current Firebase user
          setUser(processUser(auth.currentUser));
        }
      }
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      // Clear all user-related data first
      localStorage.removeItem('userData');
      localStorage.removeItem('impersonationData');
      
      // Clear any other app-specific cached data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('user_') || key.includes('cached'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Reset state
      setUser(null);
      setImpersonationStatus({ isImpersonating: false });
      
      // Sign out from Firebase (this will trigger onAuthStateChanged)
      await firebaseSignOut(auth);
      
      console.log('User logged out and cache cleared');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if Firebase signout fails, clear local state
      setUser(null);
      setImpersonationStatus({ isImpersonating: false });
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated: !!user, 
        user, 
        loading,
        impersonationStatus,
        setUser: setUserData,
        setImpersonationStatus,
        refreshContext,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};